import './oligo-config-detail.scss';

import { addDays, format } from 'date-fns';

import { ThunkDispatch } from 'redux-thunk';

import * as Model from '../../models';
import * as I18n from '../../models/i18n';
import * as ModelUtil from '../../models/util';
import * as OligoActions from '../../actions/oligo-actions';
import * as WorldActions from '../../actions/world-actions';
import * as OligoModalSupport from '../../components/oligo-modal-support/oligo-modal-support';
import { CalculationResponse, CalculationService, CutoffTimeDuration } from '../../service/calculation-service';
import { element } from 'angular';

/**
 * Start of component definition types
 */

const MIXED_BASE_SELECTED: string = 'mix-base-selected';

type MixedBase = {
  name: string;
  value: string;
};

type MixedBasesPanelData = {
  bases: MixedBase[];
};

type TextInputError = {
  invalidList: string[];
  invalidLength: {
    min: boolean;
    max: boolean;
  };
  otherErrors: string[];
};
/**
 * End of component definition types
 */

type MixedBasesPanelProps = {
  data: MixedBasesPanelData;
  visible: boolean;
  rect: {
    right: number;
    height: number;
    width: number;
    top: number;
  };
};

type ScopeWatcher = () => void;

type OligoPropertiesCalculation = {
  data?: CalculationResponse;
  ignore: boolean;
};

export interface SelectionItem {
  id: string;
  name: string;
  selected: boolean;
  paramDef: Model.ClientParamDef<string>;
}

type DisplayType = Model.OligoDisplayType;

const DisplayTypes: {
  SUMMARY: DisplayType;
  DETAILS: DisplayType;
} = Model.OligoDisplayTypes;

type TextLengthConstraints = {
  min?: number;
  max?: number;
};

type TextInputConstraints = {
  length?: TextLengthConstraints,
  symbols?: {
    valid: string[],
  },
};

type TextInputData<T extends Model.TextInputClientParamDef> = {
  definition: T;
  possibleErrors?: TextInputError;
  constraints?: TextInputConstraints;
};

type DropDownSelectionData = {
  definition: Model.SelectionClientParamDef;
  items: Model.DropdownObj;
};

type ToggleSelectionData<T extends object, U extends object> = {
  definition: Model.SelectionTypeClientParamDef<T, U>;
  items: SelectionItem[];
};

type SingleToggleSelectionData<T extends object, U extends object> = {
  selectedId: string;
} & ToggleSelectionData<T, U>;

type OligoConfigData = {
  index: number;
  displayType: DisplayType;
  researcherName: TextInputData<Model.TextInputClientParamDef>;
  oligoName: TextInputData<Model.TextInputClientParamDef>
  oligoValue: TextInputData<Model.SkuTextInputClientParamDef>;
  scale: DropDownSelectionData;
  purification: DropDownSelectionData;
  format: DropDownSelectionData;
  prime5Mod: DropDownSelectionData;
  prime3Mod: DropDownSelectionData;
  delivery: SingleToggleSelectionData<Model.SkuClientParamDef<string>, Model.SkuClientParamDef<string>>
  alternate: ToggleSelectionData<Array<Model.ClientParamDef<string>>, Model.ClientParamDef<string>>;
  handling: ToggleSelectionData<Array<Model.SkuClientParamDef<string>>, Model.SkuClientParamDef<string>>;
  properties: {
    tm: {
      at50mM?: number;
      at1M?: number;
      available: boolean;
    };
    gc: {
      value?: number;
      available: boolean;
    };
    yieldAmount: {
      ug?: number;
      nmole?: number;
      available: boolean;
    };
    numBases: number;
    cutoffTimeDuration?: CutoffTimeDuration | null;
    turnAroundTimeDisplay: string | null;
  };
  moreOptions: boolean;
  invalid: boolean;
};

type OligoPropertiesCalculator = (opticalDensity: number, sequence?: string, delayOverride?: number) => ng.IPromise<OligoPropertiesCalculation>;
type CutoffDurationSubscriber = (duration?: CutoffTimeDuration | null) => void;
type CutoffTimeDurationSubscription = {
  subscribe(cutoffTimeUnix: number, subscriber: CutoffDurationSubscriber): void;
  unsubscribe(): void;
};

function singleSelectionClientParamDefToSelectionItems(selectionDef: Model.SelectionClientParamDef): SelectionItem[] {
  const { value: options, selected } = selectionDef;
  return options.map((option: Model.ClientParamDef<string>): SelectionItem => {
    const { id, name, descriptions } = option;
    const displayName: string = descriptions && descriptions.length > 0 ? descriptions.join(' ') : name;
    return { id, name: displayName, selected: selected ? selected.id === id : false, paramDef: option };
  });
}

function multiSelectionClientParamDefToSelectionItems(selectionDef: Model.SelectionTypeClientParamDef<Array<Model.ClientParamDef<string>>, Model.ClientParamDef<string>>): SelectionItem[] {
  const { value: options, selected } = selectionDef;
  return options.map((option: Model.ClientParamDef<string>): SelectionItem => {
    const { id, name, descriptions } = option;
    const displayName: string = descriptions && descriptions.length > 0 ? descriptions.join(' ') : name;
    const found: Model.ClientParamDef<string> | undefined = selected.find((selectedOption: Model.ClientParamDef<string>) => selectedOption.id === id);
    return { id, name: displayName, selected: found ? true : false, paramDef: option };
  });
}

function sortByEnabled(dropdown: Model.DropdownObj, paramDef: Model.SelectionClientParamDef): Model.DropdownObj {
  const noneParamDef: Model.SkuClientParamDef<string> | undefined =
    paramDef.value.find((value: Model.SkuClientParamDef<string>) =>
      value && typeof value.id === 'string' && value.id.length === 0);
  dropdown.options.sort((dropdownContent1: Model.DropdownContentObj, dropdownContent2: Model.DropdownContentObj) => {
    if (noneParamDef && noneParamDef.name === dropdownContent1.title) {
      return -1;
    } else if (noneParamDef && noneParamDef.name === dropdownContent2.title) {
      return 1;
    } else if (dropdownContent1.disabled === dropdownContent2.disabled) {
      return dropdownContent1.title.localeCompare(dropdownContent2.title);
    } else if (!dropdownContent1.disabled && dropdownContent2.disabled) {
      return -1;
    } else {
      return 1;
    }
  });
  return dropdown;
}

function textInputConstraintsFrom(definition: Model.TextInputClientParamDef): TextInputConstraints {
  return {
    length: (definition.textLimits || []).reduce((constraints: TextLengthConstraints, textLimitsDef: Model.TextLimitDef) => {
      const { min, max } = textLimitsDef;
      if (min > (constraints.min as number)) {
        constraints.min = min;
      }
      if (max < (constraints.max as number)) {
        constraints.max = max;
      }
      return constraints;
    }, { min: 0, max: Infinity }),
    symbols: {
      valid: (definition.textSymbols || []).reduce((validSymbols: string[], symbolsDef: Model.TextSymbolsDef) => {
        validSymbols = validSymbols.concat(symbolsDef.valid);
        return validSymbols;
      }, []),
    },
  };
}

function oligoPropertiesCalculator($q: ng.IQService, $timeout: ng.ITimeoutService, oligoAnalysis: CalculationService, delay: number): OligoPropertiesCalculator {
  let timeoutSubscription: ng.IPromise<void> | null;
  let deferred: ng.IDeferred<OligoPropertiesCalculation>;
  return (opticalDensity: number, sequence?: string, delayOverride?: number): ng.IPromise<OligoPropertiesCalculation> => {
    if (timeoutSubscription) {
      $timeout.cancel(timeoutSubscription);
      if (deferred) {
        deferred.resolve({ ignore: true });
      }
    }
    deferred = $q.defer();
    timeoutSubscription = $timeout(() => {
      if (!sequence || sequence.length <= 0) {
        deferred.resolve({ ignore: false });
        timeoutSubscription = null;
      } else {
        oligoAnalysis.getOligoSequenceCalculations(sequence.toUpperCase(), opticalDensity)
          .then((data: CalculationResponse) => {
            deferred.resolve({ ignore: false, data } );
          }, deferred.reject)
          .finally(() => {
            timeoutSubscription = null;
          });
      }
    }, typeof delayOverride === 'number' ? delayOverride :  delay);
    return deferred.promise;
  };
}

function cutoffTimeDurationCalculator($interval: ng.IIntervalService, calculator: CalculationService, interval: number): CutoffTimeDurationSubscription {
  let intervalSubscriber: ng.IPromise<object> | null;
  return {
    subscribe: (cutoffTimeUnix: number, subscriber: CutoffDurationSubscriber): void => {
      if (intervalSubscriber) {
        $interval.cancel(intervalSubscriber);
      }
      if (cutoffTimeUnix < 0) {
        subscriber(null);
        return;
      }
      const computeDuration = (() => {
        let duration: CutoffTimeDuration;
        return () => {
          duration = calculator.getRemainingCutoffTime(cutoffTimeUnix);
          // if the timer rollsover, meaning it has reached the cutoff time,
          // we need to adjust the timer to rollover to the next cutoff
          const rollOver: boolean = duration.secs < 0 ||
                                    duration.mins < 0 ||
                                    duration.hrs < 0;
          if (rollOver) {
            cutoffTimeUnix = addDays(new Date(cutoffTimeUnix), 1).getTime();
            duration = calculator.getRemainingCutoffTime(cutoffTimeUnix);
            duration.rollOver = rollOver;
          }
          subscriber(duration);
        };
      })();
      // initial data
      computeDuration();
      intervalSubscriber = $interval(computeDuration, interval);
    },
    unsubscribe: () => {
      if (!intervalSubscriber) {
        return;
      }
      $interval.cancel(intervalSubscriber);
      intervalSubscriber = null;
    },
  };
}

function trimSpacesConvertUpper(text: string): string {
  return text.trim().replace(/\s|\r?\n|\r/g, '').toUpperCase();
}

function textToBlocks(text: string, blockNum: number): string[] {
  const sequenceBlock: string[] = [];
  if (!text) {
    return sequenceBlock;
  }
  text = trimSpacesConvertUpper(text);
  if (text.length <= 0) {
    return sequenceBlock;
  }
  let block: string = '';
  let basesGroupCount: number = 0;
  let basesGroup = '';
  let insideGroup = false;
  let numOpeningBrace = 0;
  let numClosingBrace = 0;
  for (let i = 0; i < text.length; i++) {
    const currentBp = text[i];
    if (currentBp === '[') {
      numOpeningBrace++;
      if (!insideGroup) {
        basesGroup = '';
      }
      insideGroup = true;
    } else if (currentBp === ']') {
      numClosingBrace++;
      if (numOpeningBrace === numClosingBrace) {
        insideGroup = false;
      }
    }
    basesGroup = basesGroup + currentBp;
    // this is the last base and the group has not
    // been closed, force it
    if (insideGroup && (i === text.length - 1)) {
      insideGroup = false;
    }

    if (!insideGroup) {
      if (basesGroupCount % blockNum === 0 && i !== 0) {
        sequenceBlock.push(block);
        block = '';
      }
      block += basesGroup;
      basesGroupCount = basesGroupCount + 1;
      basesGroup = '';
    }
  }
  sequenceBlock.push(block);
  return sequenceBlock;
}

function toLocaleDateString(date: Date, locale: string, formatAsOptions: Intl.DateTimeFormatOptions, formatAsString: string): string {
  try {
    return date.toLocaleDateString(locale, formatAsOptions);
  } catch (e) {
    return format(date, formatAsString);
  }
}

const OLIGO_PROPS_CALC_DELAY: number = 1000;
const CUTOFF_TIME_UPDATE_INTERVAL = 60000;
const SHIP_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
const SHIP_FORMAT_STRING: string = 'MMMM D';

type AppStateAware = {
  appState: Model.AppState;
  oligoInFocus?: Model.OligoInFocusData;
  validBases3Prime?: string[];
};

type I18nAware = {
  language: string;
  country: string;
  SAMPLE_LABEL: string;
  MIXED_BASES_LABEL: string;
  MIXED_BASES_PANEL_TITLE: string;
  MIXED_BASES_PANEL_DESC: string;
  MIXED_BASES_POPOVER_DISCLAIMER_TITLE: string;
  MIXED_BASES_POPOVER_DISCLAIMER_TPL: string;
  MIXED_BASES_POPOVER_OK_LABEL: string;
  MIXED_BASES_PANEL_DISCLAIMER_LABEL: string;
  BASE_COUNT_LABEL: string;
  MIN_YIELD_LABEL: string;
  PRICE_LABEL: string;
  PRICE_DESC: string;
  PRICE_LOADING_LABEL: string;
  SHOW_MORE_OPTION_LABEL: string;
  SHOW_LESS_OPTION_LABEL: string;
  UNNAMED_OLIGO_LABEL: string;
  UNNAMED_RESEARCHER_LABEL: string;
  SEQUENCE_LABEL: string;
  SEQUENCE_EMPTY_LABEL: string;
  DELETE_POPOVER_TITLE_OLIGO: string;
  DELETE_POPOVER_DESC_OLIGO: string;
  POPOVER_LABEL_DELETE_CONFIRM: string;
  POPOVER_CANCEL_LABEL: string;
  TAT_SHIP_BY_LABEL_TPL: string;
  TAT_CUTOFF_LABEL_TPL: string;
  TAT_SHIP_BY_LABEL?: string;
  TAT_CUTOFF_LABEL?: string;
  INVALID_BP_MSG: string;
  SEQUENCE_TOO_SHORT_MSG_TPL: string;
  SEQUENCE_TOO_LONG_MSG_TPL: string;
  SEQUENCE_TOO_SHORT_MSG?: string;
  SEQUENCE_TOO_LONG_MSG?: string;
  ERROR_INVALID_CONFIGURATION: string;
  OLIGO_NAME_PLACEHOLDER: string;
  RESEARCHER_NAME_PLACEHOLDER: string;
  OLIGO_SEQUENCE_PLACEHOLDER: string;
  SCALE_TOOLTIP_TEXT: string;
  PURIFICATION_TOOLTIP_TEXT: string;
};

type DispatchSupport = OligoActions.OligoUpdateSupport & OligoActions.OligoDeleteSupport & WorldActions.AppStateUpdateSupport;

type OnComponentLayoutFn = (oligo: { data: Model.OligoItem }) => void;
export class OligoConfigDetailController implements ng.IComponentController, AppStateAware, DispatchSupport, I18nAware {

  public projectIdx?: number;
  public oligo?: Model.OligoItem | null;
  public data?: OligoConfigData | null;
  public price?: Model.Pricing | null;
  public oligoInFocus?: Model.OligoInFocusData;
  public updateOligo: OligoActions.OligoUpdate | null = null;
  public deleteOligo: OligoActions.OligoDelete | null = null;
  public updateAppState: WorldActions.AppStateUpdate | null = null;
  public updateOligoDisplayState: OligoActions.OligoUpdateDisplayState | null = null;
  public onComponentLayout: OnComponentLayoutFn | null = null;
  public mixedBasesPanelProps: MixedBasesPanelProps = {
    data: {
      bases: [],
    },
    visible: false,
    rect: {
      right: 0,
      height: 0,
      width: 0,
      top: 0,
    },
  };
  public loading: boolean = false;
  public validBases3Prime: string[] = [];
  public textToBlocks: (text: string, blockNum: number) => string[] = textToBlocks;
  public formatAmount: ModelUtil.AmountFormatter = ModelUtil.formatAmount;

  public appState: Model.AppState = Model.AppStates.DRAFT;

  public language: string = 'en';
  public country: string = 'us';
  public SAMPLE_LABEL: string = 'See sample label';
  public MIXED_BASES_LABEL: string = 'Add Mixed Bases';
  public MIXED_BASES_PANEL_TITLE: string = 'Mixed bases';
  public MIXED_BASES_PANEL_DESC: string = 'Click on a mixed base single letter code to add it to your sequence';
  public BASE_COUNT_LABEL: string = 'Base Count';
  public MIN_YIELD_LABEL: string = 'Minimum Yield';
  public PRICE_LABEL: string = 'PRICE';
  public PRICE_DESC: string = 'Product pricing is available in the shopping cart.';
  public PRICE_LOADING_LABEL: string = 'Calculating price...';
  public SHOW_MORE_OPTION_LABEL: string = 'Show more options';
  public SHOW_LESS_OPTION_LABEL: string = 'Show fewer options';
  public UNNAMED_OLIGO_LABEL: string = 'Unnamed Oligo';
  public UNNAMED_RESEARCHER_LABEL: string = 'Unnamed Researcher';
  public SEQUENCE_LABEL: string = 'Oligo Sequence';
  public SEQUENCE_EMPTY_LABEL: string = 'EMPTY';
  public DELETE_POPOVER_TITLE_OLIGO: string = 'Delete Oligo(s)';
  public DELETE_POPOVER_DESC_OLIGO: string = 'Are you sure you want to delete the oligo(s)? This action cannot be undone.';
  public POPOVER_LABEL_DELETE_CONFIRM: string = 'Yes';
  public POPOVER_CANCEL_LABEL: string = 'Cancel';
  public TAT_SHIP_BY_LABEL_TPL: string = '<span class="turnaround-time">Ship by <span class="date-time">{{SHIP_BY_DATE}}.</span></span>';
  public TAT_CUTOFF_LABEL_TPL: string = '<span class="turnaround-time">Order within <span class="date-time">{{CUTOFF_COUNTDOWN}}.</span></span>';
  public TAT_SHIP_BY_LABEL: string = '';
  public TAT_CUTOFF_LABEL: string = '';
  public INVALID_BP_MSG: string = 'not a recognized base.';
  public SEQUENCE_TOO_SHORT_MSG_TPL: string = 'You must enter at least {{MIN_LENGTH}} base pairs.';
  public SEQUENCE_TOO_LONG_MSG_TPL: string = 'A maximum of {{MAX_LENGTH}} bases is allowed for the selected scale and options.';
  public SEQUENCE_TOO_SHORT_MSG: string = '';
  public SEQUENCE_TOO_LONG_MSG: string = '';
  public MIXED_BASES_POPOVER_OK_LABEL: string = 'Ok';
  public MIXED_BASES_PANEL_DISCLAIMER_LABEL: string = 'Important note on adding mixed/randomized bases to your oligo ';
  public MIXED_BASES_POPOVER_DISCLAIMER_TITLE: string = 'Important note on adding mixed/randomized bases to your oligo ';
  public MIXED_BASES_POPOVER_DISCLAIMER_TPL: string = `
    <p>
    Thermo Fisher offers mixed or randomized base pairs for designing custom oligos.
    We offer two processes for randomization: automated mixing and hand mixing. When ordering oligos on thermofisher.com, the default is automated mixing.
    </p>
    <p>
    Automated mixing uses machine mixing for adding degenerate or random bases into the oligo during synthesis.
    This service is at no additional charge to the oligo and can be added at all base sites except at the 3’ end.
    The synthesizer disperses an equal ratio of the bases but due to differing binding affinities between A, C, G, and T, some bases may couple more frequently than others.
    <strong>As a result, we do not guarantee an equal ratio of the bases in oligos being synthesized using automated mixing.</strong>
    </p>
    <p>
    If you need an equal or specific ratio of bases, we suggest our hand-mixing option offered through our Specialty Oligo Services Lab.
    Pricing varies for hand-mixing options and can be quoted through our Oligo Inquiries Team.
    For a quote on hand-mixing, you can contact <a href="mailto:{{APP_SUPPORT_EMAIL}}">{{APP_SUPPORT_EMAIL}}</a>
    </p>
  `;
  public ERROR_INVALID_CONFIGURATION: string = 'Invalid configuration';
  public OLIGO_NAME_PLACEHOLDER: string = 'Oligo Name Placeholder';
  public RESEARCHER_NAME_PLACEHOLDER: string = 'Researcher Name Placeholder';
  public OLIGO_SEQUENCE_PLACEHOLDER: string = 'Input oligo sequence (5’ to 3’) and press `Enter` or select configuration options to refresh price.';
  public SCALE_TOOLTIP_TEXT: string = 'Please choose configurations from left to right starting with synthesis scale.';
  public PURIFICATION_TOOLTIP_TEXT: string = 'Purification options are dependent on synthesis scale. They also determine availability of 3’ Mods and 5’ Mods.';

  constructor($scope: ng.IScope,
              $q: ng.IQService,
              $timeout: ng.ITimeoutService,
              $interval: ng.IIntervalService,
              $ngRedux: Model.INgRedux,
              oligoActions: OligoActions.OligoActions,
              worldActions: WorldActions.WorldActions,
              oligoAnalysis: CalculationService,
              private oligoModalSupport: OligoModalSupport.OligoModalSupport | null) {
    const bindDispatchers = (dispatch: ThunkDispatch<Model.RootState, object, Model.ReduxAction<object>>) => {
      this.updateOligo = (request: OligoActions.OligoUpdateFnRequest) => {
        return dispatch(oligoActions.updateOligo(request));
      };
      this.deleteOligo = (request: OligoActions.OligoDeleteFnRequest) => {
        return dispatch(oligoActions.deleteOligo(request));
      };
      this.updateAppState = (request: WorldActions.AppStateUpdateFnRequest) => {
        return dispatch(worldActions.updateAppState(request));
      };
      this.updateOligoDisplayState = (request: OligoActions.OligoUpdateDisplayStateFnRequest) => {
        return dispatch(oligoActions.updateOligoDisplayState(request));
      };
    };
    const unsubscribeRedux: ScopeWatcher = $ngRedux.connect(this.mapStateToThis.bind(this), bindDispatchers)(this);
    const calculateOligoProperties: OligoPropertiesCalculator =
      oligoPropertiesCalculator($q, $timeout, oligoAnalysis, OLIGO_PROPS_CALC_DELAY);
    const cutoffTimeDurationSubscription: CutoffTimeDurationSubscription =
      cutoffTimeDurationCalculator($interval, oligoAnalysis, CUTOFF_TIME_UPDATE_INTERVAL);
    let scopeWatchers: ScopeWatcher[] = [];

    $scope.$on('$destroy', () => {
      scopeWatchers.forEach((unwatch: ScopeWatcher) => unwatch());
      scopeWatchers.length = 0;
      cutoffTimeDurationSubscription.unsubscribe();
      unsubscribeRedux();
    });

    $scope.$on(MIXED_BASE_SELECTED, (event: ng.IAngularEvent, selected: string) => {
      if (!this.oligo || !selected || selected.length <= 0) {
        return;
      }
      const { definition } = this.oligo;
      const { basic } = definition;
      const { oligoValue } = basic;
      const sequence: string = `${oligoValue.value}${selected.toUpperCase()}`;
      this.publishOligoUpdate(oligoValue.id, [{ id: oligoValue.id, value: sequence }]);
    });

    // Cleanup watchers
    function cleanupWatchers(watchers: ScopeWatcher[]) {
      watchers.forEach((unwatch) => unwatch());
      watchers.length = 0;
    }

    // create data
    function createOligoConfigData(definition: Model.OligoParamDefs , basic: any, advanced: any, index: number,
                                   displayType: DisplayType, moreOptions: boolean, turnAroundDate: Date | null,
                                   language: string, data: any): OligoConfigData {
      const constraints: TextInputConstraints = textInputConstraintsFrom(basic.oligoValue);
      return {
        index,
        displayType,
        moreOptions,
        properties: {
          tm: {
            available: false,
          },
          gc: {
            available: false,
          },
          yieldAmount: {
            available: false,
          },
          numBases: basic.oligoValue ? ModelUtil.countSequenceLength(basic.oligoValue.value) : 0,
          turnAroundTimeDisplay: turnAroundDate ? toLocaleDateString(turnAroundDate, language, SHIP_FORMAT_OPTIONS, SHIP_FORMAT_STRING) : null,
        },
        researcherName: {
          definition: basic.researcherName,
        },
        oligoName: {
          definition: basic.oligoName,
        },
        oligoValue: {
          constraints,
          definition: basic.oligoValue,
          possibleErrors: {
            invalidLength: {
              max: data ? (data.oligoValue.possibleErrors ?
                data.oligoValue.possibleErrors.invalidLength.max : false) : false,
              min: data ? (data.oligoValue.possibleErrors ?
                data.oligoValue.possibleErrors.invalidLength.min : false) : false,
            },
            invalidList: data ? (data.oligoValue.possibleErrors ?
              data.oligoValue.possibleErrors.invalidList : []) : [],
            otherErrors: (basic.oligoValue.errors ? [...basic.oligoValue.errors] : []),
          },
        },
        scale: {
          definition: basic.scale,
          items: ModelUtil.selectionClientParamDefToDropDownContent(basic.scale),
        },
        purification: {
          definition: basic.purification,
          items: ModelUtil.selectionClientParamDefToDropDownContent(basic.purification),
        },
        format: {
          definition: basic.format,
          items: ModelUtil.selectionClientParamDefToDropDownContent(basic.format),
        },
        prime3Mod: {
          definition: basic.prime3Mod,
          items: sortByEnabled(ModelUtil.selectionClientParamDefToDropDownContent(basic.prime3Mod), basic.prime3Mod),
        },
        prime5Mod: {
          definition: basic.prime5Mod,
          items: sortByEnabled(ModelUtil.selectionClientParamDefToDropDownContent(basic.prime5Mod), basic.prime5Mod),
        },
        delivery: {
          definition: basic.delivery,
          items: singleSelectionClientParamDefToSelectionItems(basic.delivery),
          selectedId: basic.delivery.selected.id,
        },
        handling: {
          definition: advanced.handling,
          items: multiSelectionClientParamDefToSelectionItems(advanced.handling),
        },
        alternate: {
          definition: advanced.alternate,
          items: multiSelectionClientParamDefToSelectionItems(advanced.alternate),
        },
        invalid: definition.status.state !== Model.OligoStates.VALID,
      };
    }

    $scope.$watch(() => this.oligo, (oligo?: Model.OligoItem | null) => {
      if (!oligo) {
        return;
      }
      // clean up previous watchers on every change
      cleanupWatchers(scopeWatchers);

      const { definition, index, pricing, displayType: oligoDisplayType, configDisplayType } = oligo;
      const { basic, advanced, properties } = definition;
      const { turnaroundTime } = properties;
      const { shipInXDays, cutoffUnixTime } = turnaroundTime;
      const turnAroundDate: Date | null = shipInXDays < 0 ? null : addDays(new Date(), shipInXDays);
      const displayType: DisplayType = this.data ? this.data.displayType || DisplayTypes.DETAILS : oligoDisplayType;
      const moreOptions: boolean = this.data ? this.data.moreOptions || false : configDisplayType === Model.OligoConfigDisplayTypes.ADVANCED;
      const constraints: TextInputConstraints = textInputConstraintsFrom(basic.oligoValue);

      // create data
      const data = createOligoConfigData(definition, basic, advanced, index, displayType, moreOptions, turnAroundDate, this.language, this.data);

      // update sequence messages
      this.updateSequenceMessages(constraints);

      const updateOligoProps = (response: OligoPropertiesCalculation) => {
        if (response.ignore) {
          return;
        }
        const { data: calculationData } = response;
        const { properties: oligoProps } = data;
        if (calculationData) {
          const { gc, tm, yield: yieldAmount } = calculationData;
          oligoProps.gc.value = parseInt(gc, 10);
          oligoProps.gc.available = true;
          oligoProps.tm.at1M = parseFloat(tm.mt10Prime);
          oligoProps.tm.at50mM = parseFloat(tm.mt5Prime);
          oligoProps.tm.available = true;
          oligoProps.yieldAmount.ug = parseFloat(yieldAmount.ug);
          oligoProps.yieldAmount.nmole = parseFloat(yieldAmount.nmole);
          oligoProps.yieldAmount.available = true;
        } else {
          oligoProps.gc.available = false;
          oligoProps.tm.available = false;
          oligoProps.yieldAmount.available = false;
        }
      };

      // Let's wrap it in a timeout so that we wait for
      // the child components to be linked first before
      // we listen to scope changes
      $timeout(() => {
        // FIXME: the sequence text component always fires an
        // event regardless of whether the the actual sequence has changed
        const textInputWatchers: ScopeWatcher[] = [
          data.researcherName,
          data.oligoName,
          data.oligoValue,
        ].map((textData: TextInputData<Model.TextInputClientParamDef>) => {
          return ((textInputData: TextInputData<Model.TextInputClientParamDef>) => {
            return $scope.$on(textInputData.definition.id, (event: ng.IAngularEvent, newText) => {
              const { id: paramDefId } = textInputData.definition;
              if (newText && paramDefId === basic.oligoValue.id) {
                newText = trimSpacesConvertUpper(newText);
              }
              const parameters: OligoActions.OligoUpdateItem[] = [{ id: paramDefId, value: newText }];
              this.publishOligoUpdate(paramDefId, parameters);
            });
          })(textData);
        });
        scopeWatchers = scopeWatchers.concat(textInputWatchers);

        const textChangeWatchers: ScopeWatcher[] = [
          data.researcherName,
          data.oligoName,
          data.oligoValue,
        ].map((textData: TextInputData<Model.TextInputClientParamDef>) => {
          return ((textInputData: TextInputData<Model.TextInputClientParamDef>) => {
            return $scope.$on(`${textInputData.definition.id}-onChange`, (event: ng.IAngularEvent, sequence?: string) => {
              const { updateAppState, appState } = this;
              if (!updateAppState || appState === Model.AppStates.EDIT) {
                return;
              }
              updateAppState(Model.AppStates.EDIT);
            });
          })(textData);
        });
        scopeWatchers = scopeWatchers.concat(textChangeWatchers);

        const selectionWatchers: ScopeWatcher[] = [
          data.scale,
          data.purification,
          data.format,
          data.prime3Mod,
          data.prime5Mod,
        ].map((dropdownData: DropDownSelectionData) => {
          return ((selectionData: DropDownSelectionData) => {
            return $scope.$watch(() => selectionData.items.selected, (selected: Model.DropdownContentObj, oldSelected: Model.DropdownContentObj) => {
              if (selected === oldSelected) {
                return;
              }
              const selectedParamDef: Model.SkuClientParamDef<string> = selectionData.definition.value
                .find((paramDef: Model.SkuClientParamDef<string>) => paramDef.name === selected.title) as Model.SkuClientParamDef<string>;
              const { id: paramDefId } = selectionData.definition;
              const parameters: OligoActions.OligoUpdateItem[] = [{ id: selectedParamDef.id, value: selectedParamDef.id }];
              this.publishOligoUpdate(paramDefId, parameters);
            });
          })(dropdownData);
        });
        scopeWatchers = scopeWatchers.concat(selectionWatchers);

        scopeWatchers.push($scope.$on(`${data.oligoValue.definition.id}-onChange`, (event: ng.IAngularEvent, sequence?: string) => {
          if (!sequence || sequence.length <= 0) {
            return;
          }
          sequence = trimSpacesConvertUpper(sequence);
          if (sequence.length <= 0) {
            return;
          }
          calculateOligoProperties(properties.opticalDensity, sequence)
            .then(updateOligoProps, console.error);
          data.properties.numBases = sequence ? ModelUtil.countSequenceLength(sequence) : 0;
        }));

        this.highlightLastBaseIfMixedBase();
      });

      calculateOligoProperties(properties.opticalDensity, data.oligoValue.definition.value, 0)
        .then(updateOligoProps, console.error);

      cutoffTimeDurationSubscription.subscribe(cutoffUnixTime, (duration?: CutoffTimeDuration | null) => {
        if (!data || !duration) {
          return;
        }
        data.properties.cutoffTimeDuration = duration;
        // if the cutoff time rolls over, make sure that we
        // also rollver the turnaround time
        if (turnAroundDate && duration.rollOver) {
          data.properties.turnAroundTimeDisplay = toLocaleDateString(addDays(turnAroundDate, 1), this.language, SHIP_FORMAT_OPTIONS, SHIP_FORMAT_STRING);
        }
      });

      const tatWatcher: ScopeWatcher = $scope.$watch(() => data.properties, (updateProperties) => {
        if (updateProperties.turnAroundTimeDisplay) {
          this.TAT_SHIP_BY_LABEL = I18n.replacePlaceHolders(this.TAT_SHIP_BY_LABEL_TPL, { [I18n.PlaceHolderKeys.SHIP_BY_DATE]: updateProperties.turnAroundTimeDisplay } );
        }
        if (updateProperties.cutoffTimeDuration) {
          let cutoffText: string = '';
          if (updateProperties.cutoffTimeDuration.hrs > 0) {
            cutoffText = `${cutoffText} ${updateProperties.cutoffTimeDuration.hrs} hr`;
          }
          cutoffText = `${cutoffText} ${updateProperties.cutoffTimeDuration.mins + 1} min`;
          this.TAT_CUTOFF_LABEL = I18n.replacePlaceHolders(this.TAT_CUTOFF_LABEL_TPL, { [I18n.PlaceHolderKeys.CUTOFF_COUNTDOWN]: cutoffText } );
        }
      }, true);
      scopeWatchers.push(tatWatcher);

      const possibleErrorsWatcher = $scope.$watch(() => data.oligoValue.possibleErrors, (possibleErrors) => {
        if (!possibleErrors) {
          return;
        }
        possibleErrors.otherErrors = (basic.oligoValue.errors ? [...basic.oligoValue.errors] : []);
      });
      scopeWatchers.push(possibleErrorsWatcher);

      this.data = data;
      this.price = pricing ? pricing : {
        amount: 0,
        available: properties.pricing.supported,
        currency: '',
      };
    });
  }

  // assign sequence length messages
  public updateSequenceMessages(constraints: any) {
    if (constraints && constraints.length) {
      this.SEQUENCE_TOO_SHORT_MSG = I18n.replacePlaceHolders(this.SEQUENCE_TOO_SHORT_MSG_TPL, { [I18n.PlaceHolderKeys.MIN_LENGTH]: `${constraints.length.min}` });
      this.SEQUENCE_TOO_LONG_MSG = I18n.replacePlaceHolders(this.SEQUENCE_TOO_LONG_MSG_TPL, { [I18n.PlaceHolderKeys.MAX_LENGTH]: `${constraints.length.max}` });
    }
  }

  public $onDestroy(): void {
    this.data = null;
    this.oligo = null;
    this.price = null;
    this.updateOligo = null;
    this.deleteOligo = null;
    this.updateAppState = null;
    this.oligoModalSupport = null;
  }

  public mixedBasesPanelClose(): void {
    this.mixedBasesPanelProps.visible = false;
  }

  public hasPossibleTextErrors(possibleErrors: TextInputError): boolean {
    const { invalidLength, invalidList, otherErrors } = possibleErrors;
    if (invalidList && invalidList.length > 0) {
      return true;
    }
    if (otherErrors && otherErrors.length > 0) {
      return true;
    }
    if (invalidLength) {
      return invalidLength.min || invalidLength.max;
    }
    return false;
  }

  public toggleDisplayType(): void {
    if (!this.data) {
      return;
    }
    const displayType: DisplayType = this.data.displayType === DisplayTypes.DETAILS ?
      DisplayTypes.SUMMARY : DisplayTypes.DETAILS;
    if (displayType === DisplayTypes.SUMMARY) {
      this.mixedBasesPanelProps.visible = false;
    }
    this.data.displayType = displayType;
    this.focusOligoName();
    const { oligo, projectIdx, updateOligoDisplayState } = this;
    const { moreOptions } = this.data;
    if (oligo && updateOligoDisplayState) {
      const { index: oligoIdx } = oligo;
      updateOligoDisplayState({
        displayType,
        oligoIdx,
        projectIdx: projectIdx as number,
        configDisplayType: moreOptions ? Model.OligoConfigDisplayTypes.ADVANCED : Model.OligoConfigDisplayTypes.BASIC,
      });
    }
  }

  public toggleMoreOptions(): void {
    if (!this.data) {
      return;
    }
    const moreOptions: boolean = !this.data.moreOptions;
    this.data.moreOptions = moreOptions;
    const { displayType } = this.data;
    const { oligo, projectIdx, updateOligoDisplayState } = this;
    if (oligo && updateOligoDisplayState) {
      const { index: oligoIdx } = oligo;
      updateOligoDisplayState({
        oligoIdx,
        displayType,
        projectIdx: projectIdx as number,
        configDisplayType: moreOptions ? Model.OligoConfigDisplayTypes.ADVANCED : Model.OligoConfigDisplayTypes.BASIC,
      });
    }
  }

  public get showDetails(): boolean {
    return this.data ? this.data.displayType === DisplayTypes.DETAILS : false;
  }

  public selectionToggled(toggleSelectionData: ToggleSelectionData<Array<Model.ClientParamDef<string>> |
                                               Model.ClientParamDef<string>, Model.ClientParamDef<string>>): void {
    const { id: paramDefId } = toggleSelectionData.definition;
    let parameters: OligoActions.OligoUpdateItem[];
    const radioSelectionData = toggleSelectionData as SingleToggleSelectionData<Model.SkuClientParamDef<string>, Model.SkuClientParamDef<string>>;
    const { selectedId } = radioSelectionData;
    // radio else checkbox
    if (selectedId) {
      parameters = [{ id: selectedId, value: selectedId }];
    } else {
      parameters = toggleSelectionData.items.filter((item: SelectionItem) => item.selected)
        .map((item: SelectionItem): OligoActions.OligoUpdateItem => {
          return { id: item.id, value: item.id };
        });
    }
    this.publishOligoUpdate(paramDefId, parameters);
  }

  public onTextFieldBlur(textFieldId: string): void {
    const { oligo, updateAppState, appState } = this;
    if (!updateAppState || !oligo) {
      return;
    }
    // we don't want to forcefully change the state
    // if an update is in progress or it has the same state
    const ignoreStateChange: boolean = appState === Model.AppStates.UPDATE ||
                                       appState === Model.AppStates.DRAFT;
    if (ignoreStateChange) {
      return;
    }
    updateAppState(Model.AppStates.DRAFT);
  }

  public publishOligoUpdate(paramDefId: string, parameters: OligoActions.OligoUpdateItem[]): void {
    const { projectIdx, oligo, updateOligo} = this;
    if (!updateOligo || !oligo) {
      return;
    }

    const { definition } = oligo;
    const { basic, advanced } = definition;
    const basicId: string = basic.id || 'basic';
    const advancedId: string = advanced.id || 'advanced';

    const childParentMapping: { [key: string]: string } = {
      [basic.researcherName.id]: basicId,
      [basic.oligoName.id]: basicId,
      [basic.oligoValue.id]: basicId,
      [basic.scale.id]: basicId,
      [basic.purification.id]: basicId,
      [basic.format.id]: basicId,
      [basic.prime3Mod.id]: basicId,
      [basic.prime5Mod.id]: basicId,
      [basic.delivery.id]: basicId,
      [advanced.alternate.id]: advancedId,
      [advanced.handling.id]: advancedId,
    };

    const request: OligoActions.OligoUpdateFnRequest = {
      parameters,
      projectIdx: projectIdx as number,
      oligoIdx: oligo.index,
      parentParameterDefId: childParentMapping[paramDefId],
      parameterDefId: paramDefId,
    };
    this.loading = true;
    updateOligo(request).finally(() => this.loading = false);
  }

  public publishOligoDelete(): void {
    const { projectIdx, oligo, deleteOligo, oligoModalSupport } = this;
    if (!oligo || !deleteOligo || !oligoModalSupport) {
      return;
    }
    const { id, index } = oligo;
    oligoModalSupport.show({
      title: this.DELETE_POPOVER_TITLE_OLIGO,
      content: this.DELETE_POPOVER_DESC_OLIGO,
      okLabel: this.POPOVER_LABEL_DELETE_CONFIRM,
      cancelLabel: this.POPOVER_CANCEL_LABEL,
      type: OligoModalSupport.OligoModalTypes.CONFIRM,
    }).then((status: OligoModalSupport.OligoModalSupportStatus) => {
      switch (status) {
        case OligoModalSupport.OligoModalSupportStatuses.OK:
          this.loading = true;
          deleteOligo({ id, index, projectIdx: projectIdx as number })
            .finally(() => this.loading = false);
          break;
        case OligoModalSupport.OligoModalSupportStatuses.CANCEL:
        case OligoModalSupport.OligoModalSupportStatuses.CLOSE:
        default:
          break;
      }
    });
  }

  public showMixedBasesDisclaimer(): void {
    const { oligoModalSupport } = this;
    if (!oligoModalSupport) {
      return;
    }
    oligoModalSupport.show({
      title: this.MIXED_BASES_POPOVER_DISCLAIMER_TITLE,
      content: this.MIXED_BASES_POPOVER_DISCLAIMER_TPL,
      okLabel: this.MIXED_BASES_POPOVER_OK_LABEL,
      type: OligoModalSupport.OligoModalTypes.INFO,
    });
  }

  public isUpdateInProgress(): boolean {
    return this.appState === Model.AppStates.UPDATE;
  }

  public highlightLastBaseIfMixedBase(): void {
    // NOTE: bound on link, do nothing when created
  }

  public synchDisclaimerText(): void {
    // NOTE: bound on link, do nothing when created
  }

  public focusOligoName(): void {
    // NOTE: bound on link, do nothing when created
  }

  private getMixedBasesData(sequenceCodes: Model.SequenceCode[]): MixedBase[] {
    return sequenceCodes.map((seqCode: Model.SequenceCode): MixedBase => {
      return { name: seqCode.name, value: seqCode.iupac };
    });
  }

  private getVocabularyValue(key: string, defaultValue: string, world: Model.WorldItem): string {
    const { vocabulary } = world;
    return vocabulary[key] || defaultValue;
  }

  private getInvalidBpMsg(world: Model.WorldItem): string {
    return I18n.replacePlaceHolders(this.getVocabularyValue(I18n.DictionaryKeys.MSG_INVALID_BASES, this.INVALID_BP_MSG, world), { [I18n.PlaceHolderKeys.INVALID_BASES]: '' });
  }

  private getSequenceTooShortMsgTpl(world: Model.WorldItem): string {
    return this.getVocabularyValue(I18n.DictionaryKeys.MSG_MIN_SEQ, this.SEQUENCE_TOO_SHORT_MSG_TPL, world);
  }

  private getSequenceTooLongMsgTpl(world: Model.WorldItem): string {
    return this.getVocabularyValue(I18n.DictionaryKeys.MSG_MAX_SEQ, this.SEQUENCE_TOO_LONG_MSG_TPL, world);
  }

  private getTatShipByLabelTpl(world: Model.WorldItem): string {
    return this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_TAT_SHIP_BY, this.TAT_SHIP_BY_LABEL_TPL, world);
  }

  private getTatCutoffLabelTpl(world: Model.WorldItem): string {
    return this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_TAT_CUTOFF, this.TAT_CUTOFF_LABEL_TPL, world);
  }

  private mapStateToThis = (state: Model.RootState): AppStateAware & I18nAware => {
    const { world } = state;
    const { appState, language, sequenceCodes, vocabulary, oligoInFocus, clientConfig, countryCode: country } = world;
    const validBases3Prime = clientConfig.validBases3Prime || [];

    this.mixedBasesPanelProps.data.bases = this.getMixedBasesData(sequenceCodes);
    this.synchDisclaimerText();

    return {
      appState,
      oligoInFocus,
      validBases3Prime,
      language,
      country,
      SAMPLE_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_SAMPLE_LABEL_LINK_TEXT, this.SAMPLE_LABEL, world),
      MIXED_BASES_POPOVER_OK_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.MIXED_BASE_POPOVER_OK_LABEL, this.MIXED_BASES_POPOVER_OK_LABEL, world),
      MIXED_BASES_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_MIXED_BASED_LINK_TEXT, this.MIXED_BASES_LABEL, world),
      MIXED_BASES_PANEL_TITLE: this.getVocabularyValue(I18n.DictionaryKeys.MIXED_BASE_POPOVER_TITLE, this.MIXED_BASES_PANEL_TITLE, world),
      MIXED_BASES_PANEL_DESC: this.getVocabularyValue(I18n.DictionaryKeys.MIXED_BASE_POPOVER_DESC, this.MIXED_BASES_PANEL_DESC, world),
      MIXED_BASES_POPOVER_DISCLAIMER_TITLE: this.getVocabularyValue(I18n.DictionaryKeys.MIXED_BASE_POPOVER_DISCLAIMER_TITLE, this.MIXED_BASES_POPOVER_DISCLAIMER_TITLE, world),
      MIXED_BASES_POPOVER_DISCLAIMER_TPL: this.getVocabularyValue(I18n.DictionaryKeys.MIXED_BASE_POPOVER_DISCLAIMER_TPL, this.MIXED_BASES_POPOVER_DISCLAIMER_TPL, world),
      MIXED_BASES_PANEL_DISCLAIMER_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.MIXED_BASE_PANEL_DISCLAIMER_LABEL, this.MIXED_BASES_PANEL_DISCLAIMER_LABEL, world),
      BASE_COUNT_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_BASE_COUNT, this.BASE_COUNT_LABEL, world),
      MIN_YIELD_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_MIN_YIELD, this.MIN_YIELD_LABEL, world),
      PRICE_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_PRICE_TITLE, this.PRICE_LABEL, world),
      PRICE_LOADING_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.LOADING_RETRIEVE_PRICE_DESC, this.PRICE_LOADING_LABEL, world),
      PRICE_DESC: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_PRICE_DESC, this.PRICE_DESC, world),
      SEQUENCE_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_COLLAPSED_SEQUENCE, this.SEQUENCE_LABEL, world),
      SEQUENCE_EMPTY_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_COLLAPSED_EMPTY_SEQUENCE, this.SEQUENCE_EMPTY_LABEL, world),
      SHOW_MORE_OPTION_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_SHOW_MORE_OPTION_LINK_TEXT, this.SHOW_MORE_OPTION_LABEL, world),
      SHOW_LESS_OPTION_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_SHOW_LESS_OPTION_LINK_TEXT, this.SHOW_LESS_OPTION_LABEL, world),
      UNNAMED_OLIGO_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_COLLAPSED_UNNANMED_OLIGO, this.UNNAMED_OLIGO_LABEL, world),
      UNNAMED_RESEARCHER_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_COLLAPSED_UNNANMED_RESEARCHER, this.UNNAMED_RESEARCHER_LABEL, world),
      DELETE_POPOVER_TITLE_OLIGO: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_OLIGO_DELETE_POPOVER_TITLE, this.DELETE_POPOVER_TITLE_OLIGO, world),
      DELETE_POPOVER_DESC_OLIGO: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_OLIGO_DELETE_POPOVER_DESC, this.DELETE_POPOVER_DESC_OLIGO, world),
      POPOVER_LABEL_DELETE_CONFIRM: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_OLIGO_DELETE_POPOVER_OK_BUTTON, this.POPOVER_LABEL_DELETE_CONFIRM, world),
      POPOVER_CANCEL_LABEL: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_OLIGO_DELETE_POPOVER_CANCEL_BUTTON, this.POPOVER_CANCEL_LABEL, world),
      ERROR_INVALID_CONFIGURATION: this.getVocabularyValue(I18n.DictionaryKeys.ERROR_INVALID_CONFIGURATION, this.ERROR_INVALID_CONFIGURATION, world),
      OLIGO_NAME_PLACEHOLDER: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_OLIGO_NAME_PLACEHOLDER, this.OLIGO_NAME_PLACEHOLDER, world),
      RESEARCHER_NAME_PLACEHOLDER: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_RESEARCHER_NAME_PLACEHOLDER, this.RESEARCHER_NAME_PLACEHOLDER, world),
      OLIGO_SEQUENCE_PLACEHOLDER: this.getVocabularyValue(I18n.DictionaryKeys.CONFIG_OLIGO_SEQUENCE_PLACEHOLDER, this.OLIGO_SEQUENCE_PLACEHOLDER, world),
      SCALE_TOOLTIP_TEXT: this.getVocabularyValue(I18n.DictionaryKeys.TOOLTIP_TEXT_SCALE_DROPDOWN, this.SCALE_TOOLTIP_TEXT, world),
      PURIFICATION_TOOLTIP_TEXT: this.getVocabularyValue(I18n.DictionaryKeys.TOOLTIP_TEXT_PURIFICATION_DROPDOWN, this.PURIFICATION_TOOLTIP_TEXT, world),
      INVALID_BP_MSG: this.getInvalidBpMsg(world),
      SEQUENCE_TOO_SHORT_MSG_TPL: this.getSequenceTooShortMsgTpl(world),
      SEQUENCE_TOO_LONG_MSG_TPL: this.getSequenceTooLongMsgTpl(world),
      TAT_SHIP_BY_LABEL_TPL: this.getTatShipByLabelTpl(world),
      TAT_CUTOFF_LABEL_TPL: this.getTatCutoffLabelTpl(world),
    };
  }
}

export const OLIGO_CONFIG_DETAIL_NAME: string = 'oligoConfigDetail';
export const OligoConfigDetail: ng.IDirectiveFactory = ($compile: ng.ICompileService, $timeout: ng.ITimeoutService) => {
  return {
    template: require('./oligo-config-detail.tpl.html'),
    controller: OligoConfigDetailController,
    controllerAs: 'oligoConfig',
    transclude: true,
    scope: {
      oligo: '<',
      projectIdx: '<',
      onComponentLayout: '&',
    },
    bindToController: true,
    link: ($scope: ng.IScope, $element: JQLite, attrs: ng.IAttributes, ctrl?: ng.IController) => {
      const controller: OligoConfigDetailController = ctrl as OligoConfigDetailController;
      const notifyComponentLayoutChange = (): void => {
        const { onComponentLayout, oligo } = controller;
        if (onComponentLayout && oligo) {
          onComponentLayout({ data: oligo});
        }
      };
      const configMainJQ = $element.find('.oligo-config-main').parent().parent();
      const mixBasesJQ = configMainJQ.find('.mix-bases-panel .mbp__container');
      const { mixedBasesPanelProps, validBases3Prime } = controller;
      const focusOligoName: () => void = () => {
        const focusTimeoutInterval: number = 200;
        setTimeout(() => {
          const showDetailsJQ = configMainJQ.find('.oligo-config-main-show-details');
          const oligoNameJQ = showDetailsJQ.find('.oligo-config-name .sequence-textbox-root .sequence-textbox__content');
          if (oligoNameJQ.length > 0) {
            oligoNameJQ.focus();
          }
        }, focusTimeoutInterval);
      };
      const attachMixBasesDisclaimerLabel = (): () => void => {
        const mixBasesHeadingJQ = mixBasesJQ.find('.mbp__header');
        const mixBasesDisclaimerJQ = mixBasesHeadingJQ.find('.mbp__disclaimertips');
        const disclaimerJQ = $compile(`
          <a class="mbp__disclaimer" style="font-size: 12px; text-decoration: underline;" href="" ng-click="oligoConfig.showMixedBasesDisclaimer()">
            ${controller.MIXED_BASES_PANEL_DISCLAIMER_LABEL}
          </a>
        `)($scope);
        mixBasesDisclaimerJQ.append(disclaimerJQ);
        return (): void => {
          disclaimerJQ.text(controller.MIXED_BASES_PANEL_DISCLAIMER_LABEL);
        };
      };
      const highlightLastBaseIfMixedBase: () => void = (): void => {
        const sequenceTextBoxSelector: string = [
          '.oligo-config-sequence-info__value',
          '.sequence-textbox-root',
          '.sequence-textbox__content',
        ].join(' ');
        const invalidBpClass: string = 'sequence-textbox__basePair--invalid';
        const invalid3PrimeMixBpClass: string = 'sequence-textbox__basePair--3prime';
        // clear all that we're previously marked as invalid 3'
        const showDetailsJQ = configMainJQ.find('.oligo-config-main-show-details');
        const prevLastMixBpJQ = showDetailsJQ.find(`${sequenceTextBoxSelector} .${invalid3PrimeMixBpClass}`);
        if (prevLastMixBpJQ.length > 0) {
          prevLastMixBpJQ.each((index, prevLastBpEl) => {
            prevLastBpEl.className = prevLastBpEl.className.split(' ')
              .filter((className) => className !== invalid3PrimeMixBpClass &&
                                     className !== invalidBpClass)
              .join(' ');
          });
        }
        const lastMixBpSelector: string = [
          sequenceTextBoxSelector,
          '.sequence-textbox__block:last-child',
          '.sequence-textbox__basePair--bold:last-child',
        ].join(' ');
        const lastMixBpJQ = configMainJQ.find(lastMixBpSelector);
        if (lastMixBpJQ.length > 0) {
          const base = lastMixBpJQ[0].textContent || '';
          const valid3PrimeBaseIdx = validBases3Prime.indexOf(base.toUpperCase());
          const invalid3PrimeBase = valid3PrimeBaseIdx < 0;
          if (invalid3PrimeBase) {
            lastMixBpJQ.addClass(`${invalidBpClass} ${invalid3PrimeMixBpClass}`);
          }
        }
      };
      const unwatchMixBasesToggle = $scope.$watch(() => mixedBasesPanelProps.visible, (visible: boolean) => {
        if (!visible) {
          return;
        }
        const showDetailsJQ = configMainJQ.find('.oligo-config-main-show-details');
        const oligoConfigBasicJQ = showDetailsJQ.find('.oligo-config-basic');
        const oligoConfigSelectionContainerJQ = oligoConfigBasicJQ.find('.oligo-config-selection-container');
        const col1El: HTMLElement = oligoConfigSelectionContainerJQ.find('.oligo-synthesis-scale')[0];
        const col2El: HTMLElement = oligoConfigSelectionContainerJQ.find('.oligo-synthesis-scale')[1];
        const col3El: HTMLElement = oligoConfigSelectionContainerJQ.find('.oligo-synthesis-scale')[2];
        const col4El: HTMLElement = oligoConfigSelectionContainerJQ.find('.oligo-synthesis-scale')[3];
        const oligoConfigSelContainerWidth: number = oligoConfigSelectionContainerJQ[0].offsetWidth;
        const elementRect: ClientRect = configMainJQ[0].getBoundingClientRect();
        const col3MarginLeftStr: string = window.getComputedStyle(col3El).marginLeft as string;
        const col3MarginLeft: number = parseInt(col3MarginLeftStr, 10);
        const mixBasesRectWidth: number = oligoConfigSelContainerWidth + 50;
        const mixBasesRectHeight: number = (elementRect.height / 2) + 187;

        mixBasesJQ.css('width', `${mixBasesRectWidth}px`);
        mixedBasesPanelProps.rect = {
          right: (elementRect.width) - (mixBasesRectWidth + 80),
          height: mixBasesRectHeight - 200,
          width: mixBasesRectWidth,
          top: col1El.offsetTop,
        };
      });
      let oligoLabelJQ: Model.BootstrapJQ | null;

      // changes for oligo dropdown tooltip text
      let scaleLabelJQ: Model.BootstrapJQ | null;
      const notifyDisplayTypeChange = (displayType: Model.OligoDisplayType): void => {
        if (displayType !== DisplayTypes.DETAILS) {
          if (oligoLabelJQ) {
            oligoLabelJQ.popover('destroy');
            oligoLabelJQ = null;
          }
          if (scaleLabelJQ) {
            scaleLabelJQ.popover('destroy');
            scaleLabelJQ = null;
          }
          return;
        }
        setTimeout(() => {
          const oligoLabel: Model.BootstrapJQ = $element.find('.oligo-config-sample-label') as Model.BootstrapJQ;
          if (oligoLabel.popover) {
            const popover: Model.BootstrapPopoverOptions = {
              html: true,
              placement: 'bottom',
              trigger: 'hover',
            };
            oligoLabel.popover(popover);
            oligoLabelJQ = oligoLabel;
          }
          // popover for tooltip text
          const scaleLabel: Model.BootstrapJQ = $element.find('.oligo-scale-dropdown-tooltiptext__scale-info--popover') as Model.BootstrapJQ;
          if (scaleLabel.popover) {
            const popover: Model.BootstrapPopoverOptions = {
              html: true,
              placement: 'bottom',
              trigger: 'hover',
            };
            scaleLabel.popover(popover);
            scaleLabelJQ = scaleLabel;
          }
          highlightLastBaseIfMixedBase();
        });
      };

      controller.synchDisclaimerText = attachMixBasesDisclaimerLabel();
      controller.highlightLastBaseIfMixedBase = highlightLastBaseIfMixedBase;
      controller.focusOligoName = focusOligoName;

      const unwatchOligoFocus = $scope.$watch(() => controller.oligoInFocus, (target?: Model.OligoInFocusData) => {
        const { oligo } = controller;
        if (!oligo || !target) {
          return;
        }
        if (oligo.id === target.id) {
          focusOligoName();
        }
      });

      // Let's wrap it in a timeout so that we wait for
      // the child components to be linked first before
      // we listen to scope changes
      $timeout(() => {
        const unwatchDisplayTypeToggle = $scope.$watch(() =>
          controller.data ? controller.data.displayType : DisplayTypes.DETAILS, notifyDisplayTypeChange);
        const unwatchPanelToggles = $scope.$watchGroup([
          () => controller.data ? controller.data.displayType : DisplayTypes.DETAILS,
          () => controller.data ? controller.data.moreOptions : false,
        ], notifyComponentLayoutChange);
        $scope.$on('$destroy', () => {
          if (oligoLabelJQ) {
            oligoLabelJQ.popover('destroy');
            oligoLabelJQ = null;
          }
          // changes for oligo dropdown tooltip text
          if (scaleLabelJQ) {
            scaleLabelJQ.popover('destroy');
            scaleLabelJQ = null;
          }
          unwatchPanelToggles();
          unwatchMixBasesToggle();
          unwatchDisplayTypeToggle();
          unwatchOligoFocus();
          controller.highlightLastBaseIfMixedBase = () => { /* do nothing */ };
          controller.focusOligoName = () => { /* do nothing */ };
        });
      });
    },
  };
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/components/oligo-config-detail/oligo-config-detail.ts